import axios from '../api/axios';

export type AgentTestMode = 'inprocess' | 'http';
export type AgentTestRunnerMode = AgentTestMode | 'both';

// Agent test runs can call LLMs and legitimately exceed the global 10s axios timeout.
const AGENT_TEST_LIST_TIMEOUT_MS = 30_000;
const AGENT_TEST_RUN_TIMEOUT_MS = 5 * 60_000;

export interface AgentTestSuite {
  suiteId: string;
  description?: string;
  defaults?: {
    context?: Record<string, unknown>;
    pluginIds?: string[];
    deviceId?: string;
    sessionId?: string;
  };
  cases: Array<{
    caseId: string;
    description?: string;
    sessionId?: string;
    tags?: string[];
    steps: Array<{
      stepId?: string;
      user: string;
      context?: Record<string, unknown>;
      pluginIds?: string[];
      deviceId?: string;
      requiresSideEffects?: boolean;
      expect?: Record<string, unknown>;
    }>;
  }>;
}

export interface AgentTestSuiteSummary {
  suiteId: string;
  description?: string;
  caseCount: number;
  cases: Array<{
    caseId: string;
    description?: string;
    tags?: string[];
    stepCount: number;
  }>;
}

export interface ListSuitesResponse {
  suites: AgentTestSuiteSummary[];
}

export interface GetSuiteResponse {
  suite: AgentTestSuite;
  suitePath: string;
}

export interface AgentTestRunResult {
  runId: string;
  suiteId: string;
  suitePath?: string;
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  mode: AgentTestRunnerMode;
  allowSideEffects: boolean;
  totals: {
    cases: number;
    passed: number;
    failed: number;
    steps: number;
    stepPassed: number;
    stepFailed: number;
  };
  caseResults: Array<{
    caseId: string;
    description?: string;
    sessionId: string;
    startedAt: string;
    finishedAt: string;
    durationMs: number;
    passed: boolean;
    stepResults: Array<{
      mode: AgentTestMode;
      stepIndex: number;
      stepId?: string;
      user: string;
      passed: boolean;
      errors: string[];
      startedAt: string;
      finishedAt: string;
      durationMs: number;
      response: {
        statusCode: number;
        body: any;
      };
    }>;
  }>;
}

export interface RunSuiteRequest {
  suiteId: string;
  mode: AgentTestRunnerMode;
  allowSideEffects: boolean;
  caseFilter?: string[];
  tagFilter?: string[];
  defaultDeviceId?: string;
}

export async function listAgentTestSuites(): Promise<ListSuitesResponse> {
  const res = await axios.get('/agent-tests/suites', { timeout: AGENT_TEST_LIST_TIMEOUT_MS });
  return res.data;
}

export async function getAgentTestSuite(suiteId: string): Promise<GetSuiteResponse> {
  const res = await axios.get(`/agent-tests/suites/${encodeURIComponent(suiteId)}`, {
    timeout: AGENT_TEST_LIST_TIMEOUT_MS,
  });
  return res.data;
}

export async function runAgentTestSuite(payload: RunSuiteRequest): Promise<AgentTestRunResult> {
  const res = await axios.post('/agent-tests/run', payload, { timeout: AGENT_TEST_RUN_TIMEOUT_MS });
  return res.data;
}
